import React from 'react'
import { AgTechQueryBuilder, useAgTechQuery, useAgTechQueryResultCombination } from '@agtech/core/data/queries/AgTechDataQueries'
import { StateProvinceDTO } from '@agtech/data/operations/OperationData'
import { OperationDataGetOperationStatesQuery } from '@agtech/data/operations/OperationDataQueries'
import { AgTechUserProfileAddress, AgTechUserProfileRoute, DefaultAgTechUserProfileAddress } from '@agtech/data/users/AgTechUserProfileData'
import { UserDataGetMyProfileQuery } from '@agtech/data/users/AgTechUserProfileQueries'
import { useUserDataUpdateAddressAction } from '@agtech/data/users/AgTechUserProfileActions'
import { AgTechButton, AgTechButtonProps, AgTechClickEventHandler } from '../../../components/Buttons/AgTechButtons'
import { AgTechFormTextControl } from '../../../components/Forms/Controls/AgTechFormTextControl'
import AgTechFormLookup from '../../../components/Forms/Controls/Lookups/AgTechFormLookup'
import AgTechFormBody from '../../../components/Forms/Layout/AgTechFormBody'
import { AgTechFormRow } from '../../../components/Forms/Layout/AgTechFormRow'
import AgTechTitleDetailHeader from '../../../components/Headers/AgTechTitleDetailHeader'
import AgTechFormDialog from '../../../components/Portals/Dialogs/AgTechFormDialog'

export type MyAddressEditDialogProps = {
    route: AgTechUserProfileRoute
}

const MyAddressEditDialog = (props: MyAddressEditDialogProps) => {
    const profileQuery = useAgTechQuery({
        query: UserDataGetMyProfileQuery,
        queryContext: { route: props.route }
    });

    const statesQuery = useAgTechQuery({
        query: OperationDataGetOperationStatesQuery
    });

    const dialogQuery = useAgTechQueryResultCombination({
        firstQuery: profileQuery,
        secondQuery: statesQuery,
        projection: (profile, states) => ({ profile, states })
    });

    const dialogAction = useUserDataUpdateAddressAction(props.route);

    return (
        <AgTechFormDialog
            width={550}
            dialogData={{
                query: dialogQuery,
                getDialogData: (d): AgTechUserProfileAddress => ({
                    ...DefaultAgTechUserProfileAddress,
                    ...d.profile.address,
                    lineTwo: d.profile.address?.lineTwo ?? ''
                }),
                getDialogContext: d => ({
                    states: d.states as StateProvinceDTO[]
                })
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d
            }}
            structure={() => ({
                header: 'Update My Address',
                submitButtonText: 'Save'
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='my-address-lines'>
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption='Address Line One'
                                required={true}
                                focused={true}
                                value={address => address.lineOne}
                                onControlValueChanged={async (e, val) => ({ ...e, lineOne: val })}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption='Address Line Two'
                                value={address => address.lineTwo}
                                onControlValueChanged={async (e, val) => ({ ...e, lineTwo: val })}
                            />
                        </AgTechFormRow>
                    </div>
                    <div id='my-address-locality'>
                        <AgTechFormRow>
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption='City'
                                required={true}
                                value={address => address.city}
                                onControlValueChanged={async (e, val) => ({ ...e, city: val })}
                            />
                            <AgTechFormLookup
                                entityType={formContext.entity}
                                control={{
                                    caption: 'State'
                                }}
                                selection={{
                                    selectionControlTitle: 'States',
                                    data: {
                                        itemQuery: AgTechQueryBuilder.buildQueryResult({
                                            data: formContext.context.states
                                        }),
                                        selectedItemId: formContext.entity.stateProvinceId?.toString(),
                                        searchItems: (item, filter) => item.name.toLowerCase().includes(filter.toLowerCase())
                                    },
                                    items: {
                                        itemText: state => state.name,
                                        itemHeight: 60,
                                        itemRendering: state => (
                                            <AgTechTitleDetailHeader
                                                title={state.name}
                                                detail={state.code}
                                                titleFontSize='font-size-small'
                                                detailFontSize='font-size-very-small'
                                                detailClasses='mt-1-half'
                                            />
                                        )
                                    },
                                    behavior: {
                                        onItemSelected: async (e, state) => ({
                                            ...e,
                                            stateProvinceId: state?.id ?? 0
                                        })
                                    },
                                    popup: {
                                        height: 320,
                                        width: 280
                                    }
                                }}
                            />
                            <AgTechFormTextControl
                                entityType={formContext.entity}
                                caption='Zip'
                                required={true}
                                value={address => address.zip}
                                onControlValueChanged={async (e, val) => ({ ...e, zip: val })}
                            />
                        </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

MyAddressEditDialog.Open = (e: AgTechClickEventHandler, props: MyAddressEditDialogProps) => e.dialogs.openDialog({
    dialog: () => <MyAddressEditDialog {...props} />
})

MyAddressEditDialog.Button = (props: MyAddressEditDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text='Update My Address'
        icon='fas fa-edit'
        {...props}
        action={e => MyAddressEditDialog.Open(e, props)}
    />
)

export default MyAddressEditDialog
